import React, { type FC, useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useTranslation } from 'react-i18next';

import { PageMetadata } from 'components/Shared/PageMetadata';
import { TextBox } from 'components/Shared/Inputs';
import { useIsTraumaCounsellor } from 'components/CriticalIncident/hooks/useIsTraumaCounsellor';
import { useAuth } from 'components/Auth/AuthProvider';
import { useSessionFlag } from 'hooks/useSessionFlag';
import { useFeatureManager } from 'hooks/useFeatureManager';

import './LearnScreen.scss';

interface LearnScreenProps {
}

interface VideosArray {
    content: string;
    title: string;
}
interface DocumentsArray {
    link: string;
    title: string;
    sub_title?: string;
    type: string;
}

interface LearnContent {
    videos?: VideosArray[],
    documents?: DocumentsArray[],
    total_mental_health?: DocumentsArray[],
    critical_incident?: LearnContent,
}

interface TabContentProps {
    content: LearnContent;
    description?: string;
    title: string;
    section: "critical-incident" | "training-videos" | "total-mental-health" | "documents-and-forms";
    searchLabel?: string;
}

const addTimestampQueryParamToUrl = (contentUrl: string) => {
    return `${contentUrl}&time=${Date.now()}`
}

interface CriticalIncidentTabContentProps extends Partial<TabContentProps> {
    documentsTitle: string;
    videosTitle: string;
}

const CriticalIncidentTabContent: FC<CriticalIncidentTabContentProps> = ({ content, documentsTitle, videosTitle }) => {
    return content?.documents?.length || content?.videos?.length ? (
        <div className="critical_incident_container_wrapper">
            {content?.documents?.length && (
                <div className="resources_container_wrapper">
                    <h2 className="resources_title global__header2">{documentsTitle}</h2>
                    <div className="resources_wrapper">
                        {
                            content?.documents
                                ?.map((document, index) =>
                                    document?.link?.length > 0 ?
                                        (
                                            <div className="resource_div" key={index}>
                                                <a className="pdf_doc global__link" target="_blank" href={document.link} rel="noopener noreferrer">{document.title?.replaceAll("&nbsp;", " ")}</a>
                                            </div>
                                        )
                                        :
                                        (
                                            <div className="resource_div" key={index}>
                                                <span className="pdf_doc global__link">{document.title}</span>
                                            </div>
                                        ))
                        }
                    </div>
                </div>
            )}
            {content?.videos?.length && (
                <div className="videos_container_wrapper">
                    <h2 className="videos_title global__header2">{videosTitle}</h2>
                    <div className="videos_wrapper">
                        {
                            content?.videos?.map((video, index) => (
                                <div className="video_div" key={index}>
                                    <iframe className="iframe" allow="autoplay 'none'" src={addTimestampQueryParamToUrl(video.content)} title={video.title} allowFullScreen></iframe>
                                    <h3 className="video_title global__header3 global__header3--light">{video.title}</h3>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )} 
        </div>
    ): null;
}

const TabContent: FC<TabContentProps> = ({ content, description = "", title, section, searchLabel }) => {
    const [filterText, setFilterText] = useState<string>("");
    if (section === "training-videos" && content?.videos?.length) {
        return (
            <div className="videos_container_wrapper">
                <h2 className="videos_title global__header2">{title}</h2>
                <p className="videos_description global__paragraph global__paragraph--light">{description}</p>
                <div className="videos_wrapper">
                    {
                        content?.videos?.map((video, index) => (
                            <div className="video_div" key={index}>
                                <iframe className="iframe" allow="autoplay 'none'" src={addTimestampQueryParamToUrl(video.content)} title={video.title} allowFullScreen></iframe>
                                <h3 className="video_title global__header3 global__header3--light">{video.title}</h3>
                            </div>
                        ))
                    }
                </div>
            </div>
        )
    } else if (content?.total_mental_health?.length || content?.documents?.length) {
        const docsKey = section === "total-mental-health" ? "total_mental_health" : "documents";
        return (
            <div className="resources_container_wrapper">
                <h2 className="resources_title global__header2">{title}</h2>
                {description && <p className="videos_description global__paragraph global__paragraph--light">{description}</p>}
                {searchLabel && <TextBox
                    label={searchLabel}
                    fullWidth
                    className="filter-textbox"
                    inputComponent="input"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                />
                }
                <div className="resources_wrapper">
                    {
                        content?.[docsKey]
                            ?.filter(doc => doc.title.toLocaleLowerCase().includes(filterText?.toLocaleLowerCase()))
                            ?.map((document, index) =>
                                document?.link?.length > 0 ?
                                    (
                                        <div className="resource_div" key={index}>
                                            <a className="pdf_doc global__link" target="_blank" href={document.link} rel="noopener noreferrer">{document.title?.replaceAll("&nbsp;", " ")}</a>
                                        </div>
                                    )
                                    :
                                    (
                                        <div className="resource_div" key={index}>
                                            <span className="pdf_doc global__link">{document.title}</span>
                                        </div>
                                    ))
                    }
                </div>
            </div>
        )
    } else return null;
}

export const LearnScreen: FC<LearnScreenProps> = () => {
    const { isCriticalIncidentFeatureEnabled } = useFeatureManager();
    const { t, i18n, ready } = useTranslation('learnScreen', { useSuspense: false });
    const { effectiveProviderData } = useAuth();
    const enableCriticalIncident = useSessionFlag<boolean | undefined>('enable-critical-incident');
    const { isTraumaCounsellor, isEAPCounsellor, isMixedCounsellor } = useIsTraumaCounsellor(effectiveProviderData?.providerId);
    const showCriticalIncidentTabs = isCriticalIncidentFeatureEnabled() && (enableCriticalIncident || isTraumaCounsellor || isMixedCounsellor);
    const showEAPTabs = isEAPCounsellor || isMixedCounsellor || (!isTraumaCounsellor && !isEAPCounsellor);

    let { pathname } = useLocation();

    let content: LearnContent | undefined = undefined;

    if (ready) {
        for (let i = 0; i < i18n.languages.length && !content; i++) {
            const learnScreen = i18n.store.data?.[i18n.languages[i]]?.learnScreen;
            if (learnScreen) {
                content = learnScreen as LearnContent;
            }
        }
    }

    useEffect(() => {
        const linkHandler: EventListener = (e: Event) => {
            if ((e.target as HTMLDivElement).className === 'resource_div') {
                e.stopPropagation();
                (e.target as HTMLDivElement).querySelector('a')?.click();
            }
        };
        document.addEventListener('click', linkHandler, true);
        return () => document.removeEventListener('click', linkHandler);
    }, []);

    return (
        <React.Fragment>
            {ready &&
                <PageMetadata
                    pageDescription={t('learn_screen_page_description')}
                    pageTitle={t('learn_screen_page_title')}
                />
            }
            <div className="global_learn_screen_wrapper">
                <h1 className="global__header1 page_title">{t('learn_center', 'Learn Center')}</h1>
                <Tabs
                    indicatorColor="primary"
                    variant="scrollable"
                    scrollButtons={false}
                    value={pathname.replace(/\/learn(\/)?/, "") || (showCriticalIncidentTabs ? "critical-incident" : "training-videos")}
                >
                    {showCriticalIncidentTabs && (
                        <Tab className="button_tab" component={Link} value="critical-incident" to="/learn/critical-incident" label={t('critical_incident_title', 'Critical Incident')}></Tab>
                    )}
                    {showEAPTabs && <Tab className="button_tab" component={Link} value="training-videos" to="/learn/training-videos" label={t('training_videos', 'Training videos')}></Tab>}
                    {showEAPTabs && !!content?.total_mental_health?.length && <Tab className="button_tab" component={Link} value="total-mental-health" to="/learn/total-mental-health" label={t('total_mental_health_title', 'Total Mental Health')}></Tab>}
                    {showEAPTabs && <Tab className="button_tab" component={Link} value="documents-and-forms" to="/learn/documents-and-forms" label={t('documents_and_forms', 'Documents and forms')}></Tab>}
                </Tabs>
                <section className="learn_container_wrapper_section">
                    <Routes>
                        <Route 
                            path="/"
                            element={
                                showCriticalIncidentTabs ? (<CriticalIncidentTabContent content={content?.critical_incident} documentsTitle={t('documents_title')} videosTitle={t('videos_title')} />) : 
                                (<TabContent
                                    content={content as LearnContent}
                                    section="training-videos"
                                    title={t('training_videos', 'Training Videos')}
                                    description={t('training_videos_desc', 'This is series of videos is about how to get started with Provider Hub.')}
                                />)
                            }
                        />
                        {showCriticalIncidentTabs && (
                            <Route
                                path="/critical-incident"
                                element={
                                    <CriticalIncidentTabContent
                                        content={content?.critical_incident}
                                        documentsTitle={t('documents_title')}
                                        videosTitle={t('videos_title')}
                                    />
                                }
                            />
                        )}
                        {showCriticalIncidentTabs && (
                            <Route
                                path="/training-videos"
                                element={
                                    <TabContent
                                        content={content as LearnContent}
                                        section="training-videos"
                                        title={t('training_videos', 'Training Videos')}
                                        description={t('training_videos_desc', 'This is series of videos is about how to get started with Provider Hub.')}
                                    />
                                }
                            />
                        )}
                        <Route
                            path="/training-videos"
                            element={
                                <TabContent
                                    content={content as LearnContent}
                                    section="training-videos"
                                    title={t('training_videos', 'Training Videos')}
                                    description={t('training_videos_desc', 'This is series of videos is about how to get started with Provider Hub.')}
                                />
                            }
                        />
                        <Route
                            path="/total-mental-health"
                            element={
                                <TabContent
                                    section="total-mental-health"
                                    content={content as LearnContent}
                                    title={t('total_mental_health_title', 'Total Mental Health')}
                                    description={t('total_mental_health_desc')}
                                    searchLabel={t('filter_label_hint')}
                                />
                            }
                        />
                        <Route
                            path="/documents-and-forms"
                            element={
                                <TabContent
                                    section="documents-and-forms"
                                    content={content as LearnContent}
                                    title={t('documents_and_forms', 'Documents and forms')}
                                    description={t('documents_and_forms_desc')}
                                    searchLabel={t('filter_label_hint')}
                                />
                            }
                        />
                    </Routes>
                </section>
            </div>
        </React.Fragment>
    )
}
